import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  Building2, ClipboardList, AlertTriangle, Users, ArrowUpRight, Loader2, MessagesSquare,
  Inbox, Gauge, DollarSign, ReceiptText, SlidersHorizontal, MessageSquareQuote, Clock, ListTodo,
} from "lucide-react";

const WIDGETS = [
  { key: "manager_flash", label: "Manager Flash", icon: Gauge },
  { key: "payroll", label: "Payroll (last 3 days)", icon: DollarSign },
  { key: "guest_comments", label: "Guest Comments", icon: MessageSquareQuote },
  { key: "invoices_pending", label: "Invoices Pending", icon: ReceiptText },
  { key: "requests_pending", label: "Requests Pending", icon: Inbox },
];
const ALL_KEYS = WIDGETS.map((w) => w.key);
const md = (s) => (s ? `${s.slice(5, 7)}/${s.slice(8, 10)}` : "—");
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString());

function StatCard({ label, value, icon: Icon, tone = "default", testid }) {
  const tones = { default: "text-foreground", warning: "text-[hsl(var(--warning))]", primary: "text-primary" };
  return (
    <Card className="rounded-none border p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-caps text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className={`mt-4 font-heading text-4xl font-black tracking-tight ${tones[tone]}`}>{value}</div>
    </Card>
  );
}

function WidgetShell({ title, icon: Icon, to, linkLabel, children, testid }) {
  return (
    <Card className="rounded-none border" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-border p-4">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight">
          <Icon className="h-4 w-4 text-primary" /> {title}
        </h2>
        {to && (
          <Link to={to} className="flex items-center gap-1 text-xs text-primary hover:underline">
            {linkLabel || "Open"} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      {children}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [props, setProps] = useState([]);
  const [comms, setComms] = useState({ unread_chat: 0, new_requests: 0 });
  const { activePropertyId: pid, setActivePropertyId: setPid } = useActiveProperty();
  const [gm, setGm] = useState(null);
  const [widgets, setWidgets] = useState(null); // null => loading
  const [customOpen, setCustomOpen] = useState(false);
  const [taskSum, setTaskSum] = useState({});
  const [cbOpen, setCbOpen] = useState(false);
  const navigate = useNavigate();
  const goCharge = () => {
    const list = stats.open_chargebacks_by_property || [];
    if (list.length === 1) { setPid(list[0].property_id); navigate("/monthly-report"); }
    else if (list.length > 1) setCbOpen(true);
  };
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/comms/summary").then((r) => setComms(r.data)).catch(() => {});
    api.get("/tasks/summary").then((r) => setTaskSum(r.data)).catch(() => {});
    api.get("/dashboard/preferences").then((r) => setWidgets(r.data.widgets || ALL_KEYS)).catch(() => setWidgets(ALL_KEYS));
  }, []);

  useEffect(() => {
    api.get("/dashboard/stats", { params: pid ? { property_id: pid } : {} })
      .then((r) => setStats(r.data)).catch(() => setStats({}));
  }, [pid]);

  const loadGm = useCallback(() => {
    if (!pid) return;
    setGm(null);
    api.get("/dashboard/gm", { params: { property_id: pid } }).then((r) => setGm(r.data)).catch(() => setGm({}));
  }, [pid]);
  useEffect(() => { loadGm(); }, [loadGm]);

  const enabled = (k) => (widgets || ALL_KEYS).includes(k);
  const openCustomize = () => { setDraft(widgets || ALL_KEYS); setCustomOpen(true); };
  const toggleDraft = (k) => setDraft((d) => (d.includes(k) ? d.filter((x) => x !== k) : [...d, k]));
  const saveCustomize = async () => {
    const ordered = ALL_KEYS.filter((k) => draft.includes(k));
    setWidgets(ordered);
    setCustomOpen(false);
    try { await api.put("/dashboard/preferences", { widgets: ordered }); toast.success("Dashboard saved"); }
    catch { toast.error("Could not save preferences"); }
  };

  if (!stats || widgets === null) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Overview</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">
            Good day, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Snapshot for {fmtDate(new Date().toISOString().slice(0, 10))}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {props.length > 1 && (
            <div className="w-56">
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger data-testid="dashboard-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" onClick={openCustomize} data-testid="customize-dashboard-btn">
            <SlidersHorizontal className="mr-2 h-4 w-4" /> Customize
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Properties" value={stats.total_properties ?? 0} icon={Building2} tone="primary" testid="stat-properties" />
        <StatCard label="Reports Today" value={stats.reports_today ?? 0} icon={ClipboardList} testid="stat-reports-today" />
        <StatCard label="Missing Today" value={stats.missing_reports_today ?? 0} icon={AlertTriangle} tone="warning" testid="stat-missing" />
        {stats.total_users != null && (
          <StatCard label="Users" value={stats.total_users} icon={Users} testid="stat-users" />
        )}
        {stats.open_chargebacks_count > 0 && (
          <button onClick={goCharge} className="text-left transition-transform hover:scale-[1.02]" data-testid="stat-chargebacks-btn">
            <StatCard
              label={`Open Chargebacks (${stats.open_chargebacks_count})`}
              value={`$${Number(stats.open_chargebacks_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={ReceiptText} tone="warning" testid="stat-chargebacks" />
          </button>
        )}
      </div>

      <Dialog open={cbOpen} onOpenChange={setCbOpen}>
        <DialogContent data-testid="chargebacks-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Open Chargebacks by Property</DialogTitle>
            <DialogDescription>Click a property to open its Monthly Report.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(stats.open_chargebacks_by_property || []).map((c) => (
              <button key={c.property_id} onClick={() => { setPid(c.property_id); navigate("/monthly-report"); }}
                className="flex w-full items-center justify-between rounded-md border border-border p-3 text-left hover:border-primary"
                data-testid={`chargeback-prop-${c.property_id}`}>
                <span className="font-medium">{c.property_name || "Property"}</span>
                <span className="text-sm text-muted-foreground">{c.count} open · <span className="font-semibold text-foreground">${Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {(stats.approaching_ot?.length > 0) && (
        <div className="border border-yellow-400/40 bg-yellow-50 p-5 dark:bg-yellow-950/30" data-testid="approaching-ot-alert">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
            <h3 className="font-heading text-base font-bold">Approaching weekly overtime</h3>
            <span className="text-xs text-muted-foreground">(32+ hrs this pay week — adjust upcoming shifts)</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {stats.approaching_ot.map((a) => (
              <Link key={`${a.property_id}-${a.employee_id}`} to="/time-clock"
                className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${a.level === "over" ? "border-red-400/50 bg-red-50 hover:bg-red-100 dark:bg-red-950/30" : "border-yellow-400/50 bg-white hover:bg-yellow-100/50 dark:bg-transparent"}`}
                data-testid={`approaching-ot-${a.employee_id}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium">{a.name}</div>
                  {props.length > 1 && <div className="truncate text-xs text-muted-foreground">{a.property_name}</div>}
                </div>
                <span className={`ml-2 shrink-0 font-mono font-bold tabular-nums ${a.level === "over" ? "text-red-600 dark:text-red-500" : "text-yellow-600 dark:text-yellow-500"}`}>
                  {a.hours.toFixed(2)} hrs
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {(stats.daily_report_overdue?.length > 0) && (
        <div className="border border-red-400/50 bg-red-50 p-5 dark:bg-red-950/30" data-testid="daily-report-overdue-alert">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <h3 className="font-heading text-base font-bold">Daily Report overdue</h3>
            <span className="text-xs text-muted-foreground">(yesterday not submitted by the reminder time)</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {stats.daily_report_overdue.map((o) => (
              <Link key={`${o.property_id}-${o.date}`} to={`/daily-reports/${o.property_id}/${o.date}`}
                className="flex items-center justify-between rounded-md border border-red-400/50 bg-white px-3 py-2 text-sm transition-colors hover:bg-red-100/50 dark:bg-transparent"
                data-testid={`daily-overdue-${o.property_id}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium">{o.property_name}</div>
                  <div className="truncate text-xs text-muted-foreground">{o.date} · {o.state === "draft" ? "in Draft" : "not started"}</div>
                </div>
                <span className="ml-2 shrink-0 font-semibold text-red-600 dark:text-red-500">Enter →</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {(gm?.birthdays?.length > 0) && (
        <div className="birthday-flicker border-2 p-4" data-testid="birthday-alert">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-lg" role="img" aria-label="celebrate">🎂</span>
            <h3 className="font-heading text-base font-bold text-emerald-800 dark:text-emerald-200">Upcoming birthday{gm.birthdays.length > 1 ? "s" : ""}</h3>
            {gm.birthdays.map((b) => (
              <span key={b.name + b.date} className="text-sm text-emerald-800 dark:text-emerald-200" data-testid={`birthday-${b.name}`}>
                <span className="font-semibold">{b.name}</span> — {b.days_until === 0 ? "today" : b.days_until === 1 ? "tomorrow" : `in ${b.days_until} days`} ({b.month_day})
              </span>
            ))}
          </div>
        </div>
      )}

      {(gm?.anniversaries?.length > 0) && (
        <div className="anniversary-flicker border-2 p-4" data-testid="anniversary-alert">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-lg" role="img" aria-label="celebrate">🎉</span>
            <h3 className="font-heading text-base font-bold text-indigo-800 dark:text-indigo-200">Work anniversar{gm.anniversaries.length > 1 ? "ies" : "y"}</h3>
            {gm.anniversaries.map((a) => (
              <span key={a.name + a.date} className="text-sm text-indigo-800 dark:text-indigo-200" data-testid={`anniversary-${a.name}`}>
                <span className="font-semibold">{a.name}</span> — {a.years} year{a.years > 1 ? "s" : ""} {a.days_until === 0 ? "today" : a.days_until === 1 ? "tomorrow" : `in ${a.days_until} days`} ({a.month_day})
              </span>
            ))}
          </div>
        </div>
      )}

      {(gm?.license_alerts?.length > 0) && (
        <div className="license-flicker border-2 p-5" data-testid="license-alert">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <h3 className="font-heading text-base font-bold text-red-700 dark:text-red-300">Licenses &amp; Permits need attention</h3>
            <span className="text-xs text-red-700/80 dark:text-red-300/80">Upload or renew these under Properties → Licenses &amp; Permits</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {gm.license_alerts.map((a) => (
              <Link key={a.type} to={`/properties/${pid}?tab=licenses&upload=${encodeURIComponent(a.type)}`}
                className="flex items-center justify-between rounded-md border border-red-400/60 bg-white/70 px-3 py-2 text-sm hover:bg-white dark:bg-black/20 dark:hover:bg-black/40"
                data-testid={`license-alert-${a.type}`}>
                <span className="min-w-0 truncate font-medium">{a.type}</span>
                <span className="ml-2 shrink-0 font-semibold text-red-600 dark:text-red-400">
                  {a.status === "missing" ? "Not uploaded"
                    : a.status === "no_date" ? "No expiry date"
                    : a.status === "expired" ? "Expired"
                    : `Expires in ${a.days_to_expiry}d`}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {((taskSum.my_open > 0) || (taskSum.team_open > 0)) && (
        <div className="border border-red-400/40 bg-red-50 p-5 dark:bg-red-950/20" data-testid="tasks-alert">
          <div className="mb-3 flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-red-600 dark:text-red-400" />
            <h3 className="font-heading text-base font-bold">Tasks</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {taskSum.my_open > 0 && (
              <Link to="/tasks" className="flex-1 rounded-md border border-red-400/50 bg-white px-4 py-3 dark:bg-transparent" data-testid="tasks-alert-mine">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{taskSum.my_open}</div>
                <div className="text-xs text-muted-foreground">assigned to you{taskSum.my_overdue > 0 ? ` · ${taskSum.my_overdue} overdue` : ""}</div>
              </Link>
            )}
            {taskSum.team_open != null && (
              <Link to="/tasks" className="flex-1 rounded-md border border-border bg-white px-4 py-3 dark:bg-transparent" data-testid="tasks-alert-team">
                <div className="text-2xl font-bold">{taskSum.team_open}</div>
                <div className="text-xs text-muted-foreground">open across company{taskSum.team_overdue > 0 ? ` · ${taskSum.team_overdue} overdue` : ""}</div>
              </Link>
            )}
          </div>
        </div>
      )}

      {(stats.unapproved_punch_days?.total > 0) && (
        <div className="border border-blue-400/40 bg-blue-50 p-5 dark:bg-blue-950/30" data-testid="unapproved-punches-alert">
          <div className="mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="font-heading text-base font-bold">{stats.unapproved_punch_days.total} unapproved timesheet shift{stats.unapproved_punch_days.total > 1 ? "s" : ""}</h3>
            <span className="text-xs text-muted-foreground">(this pay period — approve before running payroll)</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {stats.unapproved_punch_days.properties.map((p) => (
              <Link key={p.property_id} to="/time-clock"
                className="group flex items-center justify-between rounded-md border border-blue-400/50 bg-white px-3 py-2 text-sm transition-colors hover:bg-blue-100/50 dark:bg-transparent dark:hover:bg-blue-950/40"
                data-testid={`unapproved-punches-${p.property_id}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium">{props.length > 1 ? p.property_name : "Time Clock"}</div>
                  <div className="truncate text-xs text-muted-foreground">{p.start} – {p.end}</div>
                </div>
                <span className="ml-2 shrink-0 font-mono font-bold tabular-nums text-blue-600 dark:text-blue-400">{p.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {(comms.unread_chat > 0 || comms.new_requests > 0) && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="comms-alerts">
          {comms.unread_chat > 0 && (
            <Link to="/chat" className="group flex items-center justify-between border border-primary/30 bg-primary/5 p-5 transition-colors hover:bg-primary/10" data-testid="alert-chat">
              <div className="flex items-center gap-3">
                <MessagesSquare className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-heading text-base font-bold">{comms.unread_chat} new chat message{comms.unread_chat > 1 ? "s" : ""}</div>
                  <div className="text-xs text-muted-foreground">Open Team Chat to catch up</div>
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
          {comms.new_requests > 0 && (
            <Link to="/requests" className="group flex items-center justify-between border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5 p-5 transition-colors hover:bg-[hsl(var(--warning))]/10" data-testid="alert-requests">
              <div className="flex items-center gap-3">
                <Inbox className="h-5 w-5 text-[hsl(var(--warning))]" />
                <div>
                  <div className="font-heading text-base font-bold">{comms.new_requests} new request{comms.new_requests > 1 ? "s" : ""}</div>
                  <div className="text-xs text-muted-foreground">Review and approve pending requests</div>
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-[hsl(var(--warning))] transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      )}

      {props.length === 0 ? (
        <Card className="rounded-none border p-12 text-center text-sm text-muted-foreground">No properties assigned yet.</Card>
      ) : gm === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {enabled("manager_flash") && (
            <WidgetShell title="Manager Flash" icon={Gauge} to="/monthly-report" linkLabel="Monthly" testid="widget-manager-flash">
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Sold</TableHead>
                      <TableHead className="text-right">Occ</TableHead>
                      <TableHead className="text-right">ADR</TableHead>
                      <TableHead className="text-right">RevPAR</TableHead>
                      <TableHead className="text-right">Room Rev</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(gm.manager_flash || []).map((r) => (
                      <TableRow key={r.date} data-testid={`flash-row-${r.date}`}>
                        <TableCell className="font-medium">{md(r.date)}</TableCell>
                        {r.has_data ? (
                          <>
                            <TableCell className="text-right font-mono text-xs">{num(r.rooms_sold)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{r.occupancy}%</TableCell>
                            <TableCell className="text-right font-mono text-xs">{money(r.adr)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{money(r.revpar)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{money(r.room_revenue)}</TableCell>
                          </>
                        ) : (
                          <TableCell colSpan={5} className="text-right text-xs italic text-muted-foreground">No report</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </WidgetShell>
          )}

          {enabled("payroll") && (
            <WidgetShell title="Payroll — Last 3 Days" icon={DollarSign} to="/payroll" linkLabel="Analysis" testid="widget-payroll">
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Labor $</TableHead>
                      <TableHead className="text-right">Min / Room</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(gm.payroll || []).map((r) => (
                      <TableRow key={r.date} data-testid={`payroll-row-${r.date}`}>
                        <TableCell className="font-medium">{md(r.date)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.has_data ? r.total_hours : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.has_data ? money(r.labor_cost) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.minutes_per_room ? r.minutes_per_room : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </WidgetShell>
          )}

          {enabled("guest_comments") && (
            <WidgetShell title="Guest Comments" icon={MessageSquareQuote} to="/guest-comments" linkLabel="All" testid="widget-guest-comments">
              <div className="divide-y divide-border">
                {(gm.guest_comments || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">No comments yet.</div>
                )}
                {(gm.guest_comments || []).map((c) => (
                  <div key={c.id} className="p-4" data-testid={`gc-${c.id}`}>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{c.guest_name || "Guest"}{c.source ? ` · ${c.source}` : ""}</span>
                      <span>{md(c.date)}</span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm">{c.comment}</p>
                  </div>
                ))}
              </div>
            </WidgetShell>
          )}

          {enabled("invoices_pending") && (
            <WidgetShell title={`Invoices Pending${gm.invoices_pending?.count ? ` (${gm.invoices_pending.count})` : ""}`} icon={ReceiptText} to="/invoices" linkLabel="All" testid="widget-invoices-pending">
              <div className="divide-y divide-border">
                {(gm.invoices_pending?.items || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">Nothing pending.</div>
                )}
                {(gm.invoices_pending?.items || []).map((i) => (
                  <div key={i.id} className="flex items-center justify-between p-4 text-sm" data-testid={`inv-${i.id}`}>
                    <div>
                      <div className="font-medium">{i.vendor_name || "Vendor"}</div>
                      <div className="text-xs text-muted-foreground">{md(i.invoice_date)}</div>
                    </div>
                    <span className="font-mono text-sm">{money(i.amount)}</span>
                  </div>
                ))}
              </div>
            </WidgetShell>
          )}

          {enabled("requests_pending") && (
            <WidgetShell title={`Requests Pending${gm.requests_pending?.count ? ` (${gm.requests_pending.count})` : ""}`} icon={Inbox} to="/requests" linkLabel="All" testid="widget-requests-pending">
              <div className="divide-y divide-border">
                {(gm.requests_pending?.items || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">Nothing pending.</div>
                )}
                {(gm.requests_pending?.items || []).map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-4 text-sm" data-testid={`req-${r.id}`}>
                    <div>
                      <div className="font-medium">{r.title}</div>
                      <div className="text-xs text-muted-foreground">by {r.created_by}</div>
                    </div>
                    <span className="font-mono text-sm">{r.estimated_cost != null ? money(r.estimated_cost) : "—"}</span>
                  </div>
                ))}
              </div>
            </WidgetShell>
          )}
        </div>
      )}

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent data-testid="customize-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Customize Dashboard</DialogTitle>
            <DialogDescription>Choose which cards to show. Your choices are saved to your account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {WIDGETS.map((w) => (
              <label key={w.key} className="flex cursor-pointer items-center gap-3 border border-border p-3 hover:bg-secondary" data-testid={`toggle-${w.key}`}>
                <Checkbox checked={draft.includes(w.key)} onCheckedChange={() => toggleDraft(w.key)} />
                <w.icon className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{w.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button onClick={saveCustomize} data-testid="save-customize-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
